import { useState, useEffect, useCallback } from 'react';
import { VideoPlayer } from '../../components/VideoPlayer';
import './media-viewer.css';

export interface MediaFile {
  key: string;
  size: number;
  uploaded: string;
  contentType: string;
}

interface MediaViewerProps {
  files: MediaFile[];
  isOpen: boolean;
  initialIndex: number;
  onClose: () => void;
}

const isMediaFile = (contentType: string): boolean => {
  return contentType.startsWith('image/') || contentType.startsWith('video/');
};

export const MediaViewer = ({ files, isOpen, initialIndex, onClose }: MediaViewerProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showControls, setShowControls] = useState(false);
  const [controlsTimeout, setControlsTimeout] = useState<NodeJS.Timeout | null>(null);

  const mediaFiles = files.filter(f => isMediaFile(f.contentType));

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, mediaFiles.length]);

  // Handle mouse movement for control visibility
  useEffect(() => {
    if (!isOpen) return;

    const showControlsTemporarily = () => {
      setShowControls(true);

      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }

      const newTimeout = setTimeout(() => {
        setShowControls(false);
      }, 3000);

      setControlsTimeout(newTimeout);
    };

    const handleMouseMove = () => {
      showControlsTemporarily();
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (controlsTimeout) {
        clearTimeout(controlsTimeout);
      }
    };
  }, [isOpen, controlsTimeout]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % mediaFiles.length);
  }, [mediaFiles.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + mediaFiles.length) % mediaFiles.length);
  }, [mediaFiles.length]);

  if (!isOpen || mediaFiles.length === 0) {
    return null;
  }

  const currentFile = mediaFiles[currentIndex];
  const isImage = currentFile.contentType.startsWith('image/');
  const isVideo = currentFile.contentType.startsWith('video/');
  const mediaUrl = `/api/files/download/${encodeURIComponent(currentFile.key)}`;

  return (
    <div className="media-viewer-overlay" onClick={onClose}>
      <div className="media-viewer-container" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="media-viewer-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        {/* Media Display */}
        <div className="media-viewer-content">
          {isImage && (
            <img
              src={mediaUrl}
              alt={currentFile.key}
              className="media-viewer-image"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.src = '';
                img.innerHTML = '❌ Failed to load image';
              }}
            />
          )}
          {isVideo && (
            <div className="media-viewer-video-wrapper">
              <VideoPlayer
                src={mediaUrl}
                title={currentFile.key}
                autoPlay
                crossOrigin
                playsInline
                aspectRatio="16 / 9"
                className="media-viewer-video-player"
              />
            </div>
          )}
        </div>

        {/* Controls - Show on hover or after mouse movement */}
        <div className={`media-viewer-controls ${showControls ? 'visible' : ''}`}>
          {/* Previous Button */}
          <button
            className="media-viewer-nav-btn media-viewer-prev"
            onClick={goToPrevious}
            aria-label="Previous media"
            title="Previous (← or ← key)"
          >
            ‹
          </button>

          {/* File Info */}
          <div className="media-viewer-info">
            <div className="media-viewer-filename">{currentFile.key}</div>
            <div className="media-viewer-counter">
              {currentIndex + 1} / {mediaFiles.length}
            </div>
          </div>

          {/* Next Button */}
          <button
            className="media-viewer-nav-btn media-viewer-next"
            onClick={goToNext}
            aria-label="Next media"
            title="Next (→ or → key)"
          >
            ›
          </button>
        </div>

        {/* Keyboard Hint */}
        <div className="media-viewer-hint">
          Use arrow keys to navigate • Esc to close
        </div>
      </div>
    </div>
  );
};

export default MediaViewer;
